import icn_line_chart from "assets/images/icons/icn_line_chart.png";

import React, { useMemo, useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";

import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import RogueAPDetectionChart from "pages/AInsight/Security/components/RogueAPDetectionChart";

const BottomActionBar = ({
  parentId,
  show,
  onCheckFormValid,
  onCancel,
  onApply,
}) => {
  useEffect(() => {
    const parentElement = document.getElementById(parentId);
    const bottomActionBarElement = document.getElementById("bottomActionBar");

    if (!parentElement || !bottomActionBarElement) return;

    const updateWidth = () => {
      bottomActionBarElement.style.width = `${parentElement.clientWidth - 32}px`;
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(parentElement);
    updateWidth();

    return () => {
      resizeObserver.disconnect();
    };
  }, [parentId]);

  return (
    <Box
      id="bottomActionBar"
      sx={{
        position: "fixed",
        bottom: "20px",
        width: 0,
        display: show ? "flex" : "none",
        justifyContent: "center",
        gap: 2,
        margin: "16px 0",
        padding: "16px",
        backgroundColor: "primary.lightBlue",
        transform: show ? "translateY(0)" : "translateY(80px)",
        transition: "transform 0.3s ease-in-out",
        zIndex: 1300,
      }}
    >
      <OutlineButton
        onClick={onCancel}
        sx={{ marginRight: "20px" }}
        width="100px"
        height="32px"
      >
        Cancel
      </OutlineButton>

      <SolidButton
        onClick={onApply}
        width="100px"
        height="32px"
        disabled={!onCheckFormValid()}
      >
        Apply
      </SolidButton>
    </Box>
  );
};

const statusLabelMap = {
  new: "New",
  investigating: "Investigating",
  known_neighbor: "Known Neighbor",
};

const RogueAPDetectionDrawer = ({
  chartData,
  summaryData = [],
  auth,
  rogueAPData,
}) => {
  const theme = useTheme();
  const columnHelper = useMemo(() => createColumnHelper(), []);
  const { sendRequest } = useDataSourceManager();

  const [loading, setLoading] = useState(true);
  const [showAllAp, setShowAllAp] = useState(false);
  const [apData, setApData] = useState([]);
  const [selectedActionMap, setSelectedActionMap] = useState({});
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const data = Array.isArray(rogueAPData)
      ? rogueAPData
      : rogueAPData
        ? [rogueAPData]
        : [];

    setApData(data);
    setSelectedActionMap({});
    setLoading(false);
  }, [rogueAPData]);

  const apTableData = useMemo(
    () => (showAllAp ? apData : apData.slice(0)),
    [showAllAp, apData],
  );

  const handleSelectStatus = (rowData, nextStatus) => {
    const bssid = rowData?.BSSID;

    if (!bssid) return;

    setSelectedActionMap((prev) => ({
      ...prev,
      [bssid]: {
        bssid,
        status: nextStatus,
      },
    }));
  };

  const handleCancel = () => {
    setSelectedActionMap({});
  };

  const handleCheckFormValid = () => {
    return Object.keys(selectedActionMap).length > 0 && !applying;
  };

  const handleApply = async () => {
    try {
      setApplying(true);

      const actions = Object.values(selectedActionMap);

      const apiPath = auth?.orgs?.id
        ? "/ents/:entId/orgs/:orgId/sites/:siteId/security/rogueApDetection/states.update"
        : "/orgs/:orgId/sites/:siteId/security/rogueApDetection/states.update";

      const requestParams = {
        entId: auth?.realm?.id,
        orgId: auth?.orgs?.id || auth?.realm?.id,
        siteId: auth?.sites?.id,
      };

      for (const item of actions) {
        const requestBody = {
          bssid: item.bssid,
          status: item.status,
        };

        await sendRequest(apiPath, uuidv4(), requestParams, requestBody);
      }

      setApData((prev) =>
        prev.map((item) => {
          const selectedAction = selectedActionMap[item.BSSID];

          if (!selectedAction) return item;

          return {
            ...item,
            status: selectedAction.status,
          };
        }),
      );

      setSelectedActionMap({});
    } catch (error) {
      console.error("Update Rogue AP status failed:", error);
    } finally {
      setApplying(false);
    }
  };

  const apColumns = useMemo(
    () => [
      columnHelper.accessor("risk", {
        header: "Risk",
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor("SSID", {
        header: "SSID",
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor("BSSID", {
        header: "BSSID",
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor("operatingBand", {
        header: "Band",
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor("operatingChannel", {
        header: "Channel",
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor("signalStrength", {
        header: "RSSI",
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor("deviceName", {
        header: "Detected-by",
        cell: (info) => info.getValue(),
      }),

      columnHelper.accessor("firstSeen", {
        header: "First Seen",
        cell: (info) => info.getValue() ?? "-",
      }),

      columnHelper.accessor("lastSeen", {
        header: "Last Seen",
        cell: (info) => info.getValue() ?? "-",
      }),

      columnHelper.accessor("status", {
        header: "Status",
        cell: ({ row }) => {
          const bssid = row.original?.BSSID;

          const selectedStatus = selectedActionMap[bssid]?.status;

          const displayStatus = selectedStatus || row.original?.status;

          return statusLabelMap[displayStatus] || "-";
        },
      }),

      columnHelper.display({
        id: "action",
        header: "Action",

        cell: ({ row }) => {
          const bssid = row.original?.BSSID;

          const currentStatus =
            selectedActionMap[bssid]?.status || row.original?.status;

          const isKnownNeighbor = currentStatus === "known_neighbor";

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <SolidButton
                onClick={() => {
                  handleSelectStatus(
                    row.original,
                    isKnownNeighbor ? "new" : "known_neighbor",
                  );
                }}
                width="110px"
                height="32px"
                sx={{
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {isKnownNeighbor ? "Unmark" : "Mark Known"}
              </SolidButton>

              <SolidButton
                onClick={() => {
                  handleSelectStatus(row.original, "investigating");
                }}
                width="100px"
                height="32px"
                sx={{
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                Ticket
              </SolidButton>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, selectedActionMap],
  );

  const apTable = useTableInstance({
    tableName: "topRogueApsDrawerTable",
    columns: apColumns,
    data: apTableData,
    enableSorting: false,
    enablePagination: false,
  });

  return (
    <Box
      id="rogueAPDetectionDrawer"
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        position: "relative",
        pb: Object.keys(selectedActionMap).length > 0 ? "96px" : 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          src={icn_line_chart}
          sx={{ width: "20px", height: "20px", mr: 1.5 }}
        />

        <Typography
          sx={{
            fontWeight: theme.typography.customFontWeight[1],
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[10],
          }}
        >
          Rogue AP Detection
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          columnGap: 6,
          rowGap: 4,
          px: 0.5,
        }}
      >
        {summaryData.map((item, index) => (
          <Box key={index}>
            <Typography
              sx={{
                fontSize: theme.typography.customFontSize[2],
                fontWeight: theme.typography.customFontWeight[3],
                color: theme.palette.primary.greyPrimary,
                mb: 0.5,
              }}
            >
              {item.label}
            </Typography>

            <Typography
              sx={{
                fontSize: theme.typography.customFontSize[6],
                lineHeight: 1.1,
                fontWeight: theme.typography.customFontWeight[3],
                color: theme.palette.primary.main,
              }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <CollapseCard title="Rogue Detections Over Time (Last 24h)">
        <Box sx={{ width: "100%" }}>
          <RogueAPDetectionChart data={chartData} />
        </Box>
      </CollapseCard>

      <CollapseCard title="Top Rogue SSIDs (Risk-Ranked)">
        <Box sx={{ width: "100%" }}>
          <TableField {...apTable} loading={loading || applying} />
        </Box>
      </CollapseCard>

      <BottomActionBar
        parentId="rogueAPDetectionDrawer"
        show={Object.keys(selectedActionMap).length > 0}
        onCheckFormValid={handleCheckFormValid}
        onCancel={handleCancel}
        onApply={handleApply}
      />
    </Box>
  );
};

export default RogueAPDetectionDrawer;
